import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function OAuthCallback() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const err = params.get("error");
    const triedSilent = sessionStorage.getItem("discord_oauth_silent") === "1";

    if (!code) {
      // Silent (prompt=none) attempt failed — likely a first-time user who hasn't
      // authorized yet. Retry ONCE with the full consent screen so they can approve.
      if (err && triedSilent) {
        sessionStorage.removeItem("discord_oauth_silent");
        api.get("/auth/discord/config").then((r) => {
          const cfg = r.data;
          if (!cfg?.enabled) { setError("Discord login is not configured."); return; }
          const redirect = encodeURIComponent(`${window.location.origin}/oauth/discord/callback`);
          const scope = encodeURIComponent("identify email guilds");
          window.location.href = `https://discord.com/oauth2/authorize?client_id=${cfg.client_id}&response_type=code&redirect_uri=${redirect}&scope=${scope}&prompt=consent`;
        }).catch(() => setError("Discord login failed."));
        return;
      }
      setError("No authorization code returned by Discord.");
      return;
    }
    sessionStorage.removeItem("discord_oauth_silent");
    const redirect_uri = `${window.location.origin}/oauth/discord/callback`;
    api.post("/auth/discord/callback", { code, redirect_uri })
      .then(({ data }) => { localStorage.setItem("crank_token", data.token); setSession(data.user); nav("/app"); })
      .catch((e) => setError(apiErr(e.response?.data?.detail) || "Discord login failed"));
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-black grid-bg flex items-center justify-center px-6">
      <div className="text-center">
        <BrandLogo className="w-12 h-12 mx-auto mb-4" />
        {error ? (
          <>
            <p className="text-red-400 mb-4">{error}</p>
            <button onClick={() => nav("/login")} className="text-volt hover:underline">Back to login</button>
          </>
        ) : (
          <p className="text-zinc-400 flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Signing you in with Discord…</p>
        )}
      </div>
    </div>
  );
}
